import { ChangeDetectionStrategy, Component, forwardRef, input, output, signal } from '@angular/core';
import {
	AbstractControl,
	ControlValueAccessor,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	ValidationErrors,
	Validator,
} from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { ImpedimentGroup } from '../../../api/app-types/impediment.app';

@Component({
	selector: 'lvr-impediment-list',
	templateUrl: './impediment-list.html',
	styleUrl: './impediment-list.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [MatCheckbox],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => ImpedimentList),
			multi: true,
		},
		{
			provide: NG_VALIDATORS,
			useExisting: forwardRef(() => ImpedimentList),
			multi: true,
		},
	],
})
export class ImpedimentList implements ControlValueAccessor, Validator {
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private _onChange = (_value: number[] | null) => {};
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private _onTouched = () => {};
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private _onValidatorChange = () => {};

	public readonly groups = input<ImpedimentGroup[]>([]);
	public readonly selectedImpediments = signal<number[]>([]);
	public readonly hasError = input<boolean>(false);
	public readonly required = input<boolean>(true);

	public readonly isDisabled = signal<boolean>(false);

	public readonly selectionChanged = output<number[]>();

	public isSelected(impedimentId: number): boolean {
		const selected = this.selectedImpediments() || [];
		return selected.includes(impedimentId);
	}

	public toggleImpediment(impedimentId: number) {
		const selected = this.selectedImpediments() || [];
		if (selected.includes(impedimentId)) {
			this.selectedImpediments.set(selected.filter((id) => id !== impedimentId));
		} else {
			this.selectedImpediments.set([...selected, impedimentId]);
		}

		const value = this.selectedImpediments();
		this._onChange(value);
		this._onTouched();
		this._onValidatorChange();
		this.selectionChanged.emit(value);
	}

	public writeValue(obj: number[] | null) {
		this.selectedImpediments.set(obj ?? []);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public registerOnChange(fn: any) {
		this._onChange = fn;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public registerOnTouched(fn: any) {
		this._onTouched = fn;
	}

	public registerOnValidatorChange?(fn: () => void): void {
		this._onValidatorChange = fn;
	}

	public validate(_control: AbstractControl): ValidationErrors | null {
		if (!this.required()) return null;

		const groups = this.groups();
		const selected = this.selectedImpediments() || [];

		if (!groups?.length) return null;

		const hasGroupWithoutSelection = groups.some((group) => {
			const groupIds = group.impediments.map((impediment) => impediment.id);
			return !groupIds.some((id) => selected.includes(id));
		});

		return hasGroupWithoutSelection ? { requiredImpedimentPerGroup: true } : null;
	}

	public setDisabledState?(isDisabled: boolean) {
		this.isDisabled.set(isDisabled);
	}
}
